/* Form Component to Add or Edit Customer details*/

import { useContext, useEffect, useState } from "react";
import { Button, Form, Select, Row, Col, DatePicker, TimePicker } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import {
  TContext,
  TDropdownOptions,
  TResponseModel,
  TUser,
} from "../../../types/config";
import {
  eMONTHMAPPING,
  eREPORTTYPE,
  eResultCode,
  eSCHEDULETYPE,
  eWEEKDAYS,
} from "../../../utils/enum";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TSchedule } from "../../../types";
import {
  AddEditReportSchedules,
  GetCustomerList,
  GetUserList,
} from "../../../utils/api.constant";
import Utils from "../../../utils";
import { UserContext } from "../../../context";
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  CloseOutlined,
} from "@ant-design/icons";
import useFetch from "../../../hooks/useFetch";
import dayjs from "dayjs";
import { STAKE_HOLDERS } from "../../../utils/constants";

type DrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
  schedule?: TSchedule;
};

export default function ScheduleReportForm(props: DrawerProps) {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [userOption, setUserOption] = useState<TDropdownOptions[]>([]);
  const [monthDays, setMonthDays] = useState<TDropdownOptions[]>([
    { label: "Start of Month", value: eMONTHMAPPING.STARTOFMONTH },
    { label: "Mid of Month", value: eMONTHMAPPING.MIDOFMONTH },
    { label: "End of Month", value: eMONTHMAPPING.ENDOFMONTH },
  ]);
  const [selectedUsers, setSelectedUsers] = useState<TDropdownOptions[]>([]);
  const [scheduleType, setScheduleType] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string>("");
  const [scheduleTimeStringV, setScheduleTimeString] = useState<string>("");
  const { post } = useFetch();
  const context: TContext = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser | undefined>(
    context?.user as TUser
  );

  useEffect(() => {
    fetchCustomerOptions();
  }, []);

  useEffect(() => {
    setActiveUser(context?.user);
    if (context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER) {
      form.setFieldValue("customerId", context?.user?.orgId);
    }
  }, [context?.user]);

  const fetchCustomerOptions = async () => {
    try {
      const payload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };
      setIsLoading(true);
      const response: TResponseModel = await post(GetCustomerList, payload);
      if (response) {
        const { data, dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const custOptions = Utils.mapDropdownOptions(data);
          setCustomerOptions(custOptions);
          if (activeUser?.stakeHolderName === STAKE_HOLDERS.IDEX) {
            form.setFieldValue("customerId", custOptions?.[0]?.value);
          }
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUserList();
    if (props?.schedule) {
      const {
        scheduleTime,
        reportDate,
        customerName,
        scheduleUserList,
        scheduleTimeString,
        ...restSchedules
      } = props?.schedule as TSchedule;

      form.setFieldsValue({
        scheduleTime: dayjs(scheduleTime),
        reportDate: dayjs(reportDate),
        customerName: customerName,
        scheduleUserList,
        ...restSchedules,
      });
      setScheduleTimeString(scheduleTimeString);
      setSelectedUsers(scheduleUserList as TDropdownOptions[]);
      setSelectedCustomer(customerName as any);
      setScheduleType(props?.schedule?.reportScheduleType as any);
    }
  }, [context?.user, props?.schedule]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update validation rules dynamically based on scheduleType
  useEffect(() => {
    if (scheduleType === eSCHEDULETYPE.DAILY) {
      form.setFieldsValue({ day: undefined, reportDate: undefined });
    } else if (scheduleType === eSCHEDULETYPE.WEEKLY) {
      form.setFieldsValue({ reportDate: undefined });
    } else if (
      scheduleType === eSCHEDULETYPE.MONTHLY ||
      scheduleType === eSCHEDULETYPE.ANNUAL
    ) {
      form.setFieldsValue({ day: undefined });
    }
  }, [scheduleType, form]);

  const { onShowToast } = useToast();

  // Handle on submit event
  const onFinish = async (values: TSchedule) => {
    try {
      const { scheduleUserList, scheduleTimeString, ...restValues } = values;

      const payload = {
        data: {
          id: props?.schedule?.id ?? 0,
          scheduleUserList: selectedUsers,
          scheduleTimeString: scheduleTimeStringV,
          ...restValues,
        },
      };
      setIsLoading(true);
      console.log({ payload });

      const response = await post(AddEditReportSchedules, payload);
      const { dataResponse } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
        props?.onCloseDrawer();
        props?.onRefreshList();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getUserList = async () => {
    try {
      const payload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };
      setIsLoading(true);

      const response = await post(GetUserList, payload);
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode === eResultCode.SUCCESS) {
        setUserOption(Utils.mapDropdownOptions(data, "id", "displayName"));
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: description,
        });
      }
    } catch (error) {
      console.log("Error while fetching user list in schedule", error);
    } finally {
      setIsLoading(false);
      console.log("User list fetched");
    }
  };

  return (
    <FormLayout>
      <Form
        autoComplete="off"
        form={form}
        name="reportForm"
        onFinish={onFinish}
        layout="vertical"
        size="large"
        onValuesChange={(changedValues) => {
          if (changedValues.scheduleType) {
            setScheduleType(changedValues.scheduleType);
          }
        }}
      >
        <FormHeader>
          <Row
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Col>
              <h3 style={{ color: "var(--bs-dark)", marginTop: "10px" }}>
                Schedule Report
              </h3>
            </Col>
            <Col>
              <Button
                type="default"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => {
                  props.onCloseDrawer();
                }}
              >
                Close
              </Button>
            </Col>
          </Row>
        </FormHeader>
        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="customerId"
                label="Customer"
                rules={[{ required: true, message: "Please choose customer" }]}
              >
                <Select
                  size="large"
                  disabled={
                    isLoading ||
                    activeUser?.stakeHolderName === STAKE_HOLDERS.CUSTOMER
                      ? true
                      : false
                  }
                  showSearch
                  placeholder="Select a customer"
                  options={customerOptions}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(value, option) => {
                    const selectedOption = option as {
                      label: string;
                      value: string;
                    };
                    setSelectedCustomer(selectedOption.label as string);
                  }}
                ></Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="reportType"
                label="Report Type"
                rules={[
                  { required: true, message: "Please select report type" },
                ]}
              >
                <Select
                  placeholder="Select report type"
                  options={[
                    {
                      label: "Overall Records Report",
                      value: eREPORTTYPE.OVERALLRECORDSREPORT,
                    },
                    // {
                    //   label: "Service Report",
                    //   value: eREPORTTYPE.SERVICEINSTALLATIONREPORT,
                    // },
                    // {
                    //   label: "Installation Report",
                    //   value: eREPORTTYPE.INSTALLATIONREPORT,
                    // },
                  ]}
                />
              </Form.Item>
            </Col>

            {/* <Col span={24}>
              <Form.Item
                name="servicePartnerId"
                label="Service Partner"
                rules={[
                  {
                    required: true,
                    message: "Please choose service partner",
                  },
                ]}
              >
                <Select
                  disabled={context?.user?.stakeHolderName === "SERVICEPARTNER"}
                  placeholder="Select Service Partner"
                  options={servicePartnerOptions}
                />
              </Form.Item>
            </Col> */}

            <Col span={24}>
              <Form.Item
                name="reportScheduleType"
                label="Schedule Type"
                rules={[
                  { required: true, message: "Please select schedule type" },
                ]}
              >
                <Select
                  placeholder="Select schedule type"
                  options={[
                    { label: "Daily", value: eSCHEDULETYPE.DAILY },
                    { label: "Weekly", value: eSCHEDULETYPE.WEEKLY },
                    { label: "Monthly", value: eSCHEDULETYPE.MONTHLY },
                    { label: "Annual", value: eSCHEDULETYPE.ANNUAL },
                  ]}
                  onChange={(value: any) => setScheduleType(value)}
                />
              </Form.Item>
            </Col>

            {(scheduleType === eSCHEDULETYPE.WEEKLY ||
              scheduleType === eSCHEDULETYPE.MONTHLY) && (
              <>
                <Col span={24}>
                  <Form.Item
                    name="day"
                    label="Day"
                    rules={[{ required: true, message: "Please select day" }]}
                  >
                    <Select
                      placeholder="Select day"
                      options={
                        scheduleType === eSCHEDULETYPE.WEEKLY
                          ? [
                              { label: "Sunday", value: eWEEKDAYS.SUNDAY },
                              { label: "Monday", value: eWEEKDAYS.MONDAY },
                              { label: "Tuesday", value: eWEEKDAYS.TUESDAY },
                              {
                                label: "Wednesday",
                                value: eWEEKDAYS.WEDNESDAY,
                              },
                              {
                                label: "Thursday",
                                value: eWEEKDAYS.THURSDAY,
                              },
                              { label: "Friday", value: eWEEKDAYS.FRIDAY },
                              {
                                label: "Saturday",
                                value: eWEEKDAYS.SATURDAY,
                              },
                            ]
                          : monthDays
                      }
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* {scheduleType === eSCHEDULETYPE.MONTHLY && (
              <Col span={24}>
                <Form.Item
                  name="month"
                  label="Day of Month"
                  rules={[
                    { required: true, message: "Please select month day" },
                  ]}
                >
                  <Select placeholder="Select month day" options={monthDays} />
                </Form.Item>
              </Col>
            )} */}

            {scheduleType === eSCHEDULETYPE.ANNUAL && (
              <>
                <Col span={24}>
                  <Form.Item
                    name="reportDate"
                    label="Report Date"
                    rules={[
                      { required: true, message: "Please select report date" },
                    ]}
                  >
                    <DatePicker
                      format={context?.user?.dateFormat?.toUpperCase()}
                      placeholder="Select report date"
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col span={24}>
              <Form.Item
                name="scheduleTime"
                label="Report Time"
                rules={[{ required: true, message: "Please select time" }]}
              >
                <TimePicker
                  format="HH:mm:ss"
                  placeholder="Select start time"
                  use12Hours={false}
                  style={{ width: "100%" }}
                  onChange={(time, timeString) => {
                    setScheduleTimeString(timeString as string);
                    form.setFieldValue("scheduleTime", time);
                  }}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="scheduleUserList" label="Schedule User">
                <Select
                  mode="multiple"
                  placeholder="Select users"
                  options={userOption}
                  onChange={(value, option) => {
                    setSelectedUsers(option as any);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="isActive"
                label="Status"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select
                  placeholder="Select status"
                  options={[
                    { label: "Active", value: 1 },
                    { label: "Inactive", value: 0 },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>

        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              className="btnDimensions"
            >
              Save
            </Button>
            <Button
              className="btnDimensions"
              type="primary"
              disabled={isLoading}
              onClick={() => {
                props.onCloseDrawer();
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
